/**
 * TLA Studios — Spinner Logo Library
 *
 * The animated nav spinner cycles through these CSS-rendered logo treatments.
 * Each one is a different visual identity expressing a facet of the brand:
 * athletic, technical, scientific, playful, brutalist, holographic.
 *
 * Curated for readability at small sizes (~50px). Logo styles that did not
 * read clearly at the spinner size were retired (quad, geo, line-art).
 *
 * To add a new logo:
 *   1. Append a new entry to LOGO_STYLES below
 *   2. Add a matching `case` to the switch in SpinnerLogo
 *
 * SpinnerLogo accepts `theme` and `brand` as props (no React context) so it
 * can be reused anywhere without circular import concerns.
 */

// ────────────────────────────────────────────────────────────
// LOGO STYLE CATALOG
// ────────────────────────────────────────────────────────────

export const LOGO_STYLES = [
  // ─── ORIGINAL READABLE STYLES (7) ─────────────────────────
  { id: "red-knock", label: "TLA", sub: "Knockout", colors: ["#EF2D2D"] },
  { id: "block", label: "TLA", sub: "Block", colors: ["#000"] },
  { id: "cyan-frame", label: "TLA", sub: "Framed", colors: ["#00F0C8"] },
  { id: "sports-blue", label: "TLA", sub: "Sports Group", colors: ["#1565C0"] },
  { id: "serif", label: "TLA", sub: "Serif", colors: ["#000"] },
  { id: "bold-text", label: "TLA", sub: "Bold", colors: ["#000"] },
  { id: "gradient", label: "TLA", sub: "Chrome", colors: ["#999", "#ccc", "#666"] },

  // ─── NEW STYLES (8) — fun, on-brand, still readable ───────
  { id: "stamp", label: "TLA", sub: "Postage", colors: ["#9AAD17"] },
  { id: "pixel", label: "TLA", sub: "Pixel", colors: ["#00F0C8", "#7B6AE8"] },
  { id: "terminal", label: "TLA", sub: "Terminal", colors: ["#9AAD17"] },
  { id: "pill", label: "TLA", sub: "Capsule", colors: ["#7B6AE8"] },
  { id: "athletic", label: "TLA", sub: "Jersey", colors: ["#EF2D2D"] },
  { id: "bento", label: "TLA", sub: "Bento", colors: ["#9AAD17", "#7B6AE8", "#F4944E"] },
  { id: "holographic", label: "TLA", sub: "Iridescent", colors: ["#7B6AE8", "#00F0C8", "#F4944E", "#EF2D2D"] },
  { id: "glitch", label: "TLA", sub: "Glitch", colors: ["#00F0C8", "#EF2D2D"] },
];

// Backwards-compat alias used by older code paths (TLAHomepage.jsx)
export const LogoStyles = LOGO_STYLES;

// ────────────────────────────────────────────────────────────
// SPINNER LOGO RENDERER
// ────────────────────────────────────────────────────────────

/**
 * Renders one logo style at a given pixel size.
 * Pure presentational component — accepts theme tokens via props.
 *
 * Props:
 *  - style: one entry from LOGO_STYLES
 *  - size: pixel size of the bounding box (default 120)
 *  - theme: the THEMES.dark / THEMES.light object from TLAHomepage
 *  - brand: the BRAND_COLORS object from TLAHomepage
 */
export function SpinnerLogo({ style, size = 120, theme, brand }) {
  const t = theme;
  const c = brand;

  const s = {
    width: size,
    height: size,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  switch (style.id) {
    // ─── Original 7 ─────────────────────────────────────────

    case "red-knock":
      return (
        <div style={{ ...s, background: "#EF2D2D", borderRadius: size > 50 ? 6 : 3 }}>
          <span
            style={{
              color: t.text,
              fontWeight: 900,
              fontSize: size * 0.45,
              fontFamily: "system-ui",
              letterSpacing: size > 50 ? -3 : -1,
            }}
          >
            TLA
          </span>
        </div>
      );

    case "block":
      return (
        <div style={s}>
          <span
            style={{
              color: "#fff",
              fontWeight: 900,
              fontSize: size * 0.5,
              fontFamily: "system-ui",
              letterSpacing: size > 50 ? -4 : -1,
            }}
          >
            TLA
          </span>
        </div>
      );

    case "cyan-frame":
      return (
        <div
          style={{
            ...s,
            border: `${size > 50 ? 3 : 2}px solid ${c.cyan}`,
            borderRadius: size > 50 ? 8 : 4,
            background: "transparent",
          }}
        >
          <span
            style={{
              color: c.cyan,
              fontWeight: 700,
              fontSize: size * 0.32,
              fontFamily: "system-ui",
            }}
          >
            TLA
          </span>
        </div>
      );

    case "sports-blue":
      return (
        <div style={{ ...s, flexDirection: "column", gap: size > 50 ? 2 : 0 }}>
          <span
            style={{
              color: "#1565C0",
              fontWeight: 800,
              fontSize: size * 0.32,
              fontFamily: "system-ui",
              letterSpacing: size > 50 ? 2 : 0,
              border: `${size > 50 ? 2 : 1}px solid #1565C0`,
              padding: `${size > 50 ? 2 : 0}px ${size > 50 ? 8 : 2}px`,
              borderRadius: size > 50 ? 4 : 2,
            }}
          >
            TLA
          </span>
          {size > 50 && (
            <span
              style={{
                color: "#1565C0",
                fontWeight: 700,
                fontSize: size * 0.11,
                fontFamily: "system-ui",
                letterSpacing: 3,
                textTransform: "uppercase",
              }}
            >
              Sports Group
            </span>
          )}
        </div>
      );

    case "serif":
      return (
        <div style={s}>
          <span
            style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: size * 0.45,
              fontFamily: "Georgia, serif",
              letterSpacing: size > 50 ? 2 : 0,
            }}
          >
            TLA
          </span>
        </div>
      );

    case "bold-text":
      return (
        <div style={s}>
          <span
            style={{
              color: "#fff",
              fontWeight: 900,
              fontSize: size * 0.55,
              fontFamily: "'Arial Black', system-ui",
              letterSpacing: size > 50 ? -2 : -1,
            }}
          >
            TLA
          </span>
        </div>
      );

    case "gradient":
      return (
        <div
          style={{
            ...s,
            background: "linear-gradient(180deg, #bbb 0%, #666 100%)",
            borderRadius: size > 50 ? 10 : 4,
            padding: size > 50 ? 8 : 2,
          }}
        >
          <span
            style={{
              color: "#fff",
              fontWeight: 900,
              fontSize: size * 0.38,
              fontFamily: "system-ui",
              letterSpacing: -1,
              textShadow: "0 2px 4px rgba(0,0,0,0.4)",
            }}
          >
            TLA
          </span>
        </div>
      );

    // ─── New 8 — fun + on-brand ─────────────────────────────

    case "stamp":
      return (
        <div style={{ ...s, padding: 3 }}>
          <div
            style={{
              width: "100%",
              height: "100%",
              border: `1.5px dashed ${c.green}`,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `${c.green}11`,
            }}
          >
            <span
              style={{
                color: c.green,
                fontWeight: 800,
                fontSize: size * 0.34,
                fontFamily: "Georgia, serif",
                letterSpacing: 1,
              }}
            >
              TLA
            </span>
          </div>
        </div>
      );

    case "pixel":
      return (
        <div style={s}>
          <span
            style={{
              color: c.cyan,
              fontWeight: 900,
              fontSize: size * 0.42,
              fontFamily: "'Courier New', Courier, monospace",
              letterSpacing: 0,
              textShadow: `1.5px 1.5px 0 ${c.purple}`,
            }}
          >
            TLA
          </span>
        </div>
      );

    case "terminal":
      return (
        <div
          style={{
            ...s,
            background: "rgba(0,0,0,0.55)",
            borderRadius: 4,
            border: `1px solid ${c.green}55`,
            paddingLeft: 5,
            paddingRight: 5,
          }}
        >
          <span
            style={{
              color: c.green,
              fontWeight: 700,
              fontSize: size * 0.3,
              fontFamily: "'DM Mono', 'Courier New', monospace",
              letterSpacing: 0,
            }}
          >
            {"> TLA"}
          </span>
        </div>
      );

    case "pill":
      return (
        <div
          style={{
            ...s,
            background: c.purple,
            borderRadius: 100,
          }}
        >
          <span
            style={{
              color: "#fff",
              fontWeight: 800,
              fontSize: size * 0.36,
              fontFamily: "system-ui",
              letterSpacing: 1,
            }}
          >
            TLA
          </span>
        </div>
      );

    case "athletic":
      return (
        <div style={s}>
          <span
            style={{
              color: "#fff",
              fontWeight: 900,
              fontSize: size * 0.5,
              fontFamily: "'Arial Black', system-ui",
              letterSpacing: -1,
              WebkitTextStroke: `2px ${c.red}`,
              textShadow: `2px 2px 0 ${c.red}, -1px -1px 0 ${c.red}`,
            }}
          >
            TLA
          </span>
        </div>
      );

    case "bento":
      return (
        <div
          style={{
            ...s,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 1,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {["T", "L", "A"].map((letter, i) => (
            <div
              key={i}
              style={{
                background: style.colors[i] || c.green,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: size * 0.44,
                  fontFamily: "system-ui",
                }}
              >
                {letter}
              </span>
            </div>
          ))}
        </div>
      );

    case "holographic":
      return (
        <div style={s}>
          <span
            style={{
              fontWeight: 900,
              fontSize: size * 0.5,
              fontFamily: "system-ui",
              letterSpacing: -1,
              backgroundImage: `linear-gradient(135deg, ${c.purple}, ${c.cyan} 35%, ${c.orange} 65%, ${c.red})`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
              filter: "drop-shadow(0 0 4px rgba(123,106,232,0.4))",
            }}
          >
            TLA
          </span>
        </div>
      );

    case "glitch":
      return (
        <div style={{ ...s, position: "relative" }}>
          <span
            style={{
              color: "#fff",
              fontWeight: 900,
              fontSize: size * 0.5,
              fontFamily: "system-ui",
              letterSpacing: -1,
              textShadow: `2px 0 0 ${c.cyan}, -2px 0 0 ${c.red}`,
            }}
          >
            TLA
          </span>
        </div>
      );

    default:
      return (
        <div style={s}>
          <span style={{ color: "#fff", fontWeight: 900, fontSize: size * 0.4 }}>TLA</span>
        </div>
      );
  }
}
